import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import { t } from 'i18next';
import { useCallback } from 'react';

import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import MessageInputShell, {
  MESSAGE_CONTROL_HEIGHT_IN_PX,
} from '~pages/Messages/components/MessageInputShell';
import MessageLengthHint from '~pages/Messages/components/MessageLengthHint';

type MessageSendRowProps = {
  canSend: boolean;
  content: string;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  isSending?: boolean;
  leadingControl?: React.ReactNode;
  onContentChange: (content: string) => void;
  onSend: () => void;
};

export default function MessageSendRow({
  canSend,
  content,
  inputRef,
  isSending = false,
  leadingControl,
  onContentChange,
  onSend,
}: MessageSendRowProps) {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onSend();
      }
    },
    [onSend],
  );

  const hasLeadingControl = Boolean(leadingControl);

  return (
    <Box
      sx={{
        alignItems: 'end',
        columnGap: 1.125,
        display: 'grid',
        gridTemplateColumns: hasLeadingControl ? 'auto 1fr auto' : '1fr auto',
      }}
    >
      {hasLeadingControl && <Box sx={{ display: 'flex' }}>{leadingControl}</Box>}

      <MessageInputShell
        sx={{
          justifyContent: 'center',
          minWidth: 0,
          minHeight: MESSAGE_CONTROL_HEIGHT_IN_PX,
          py: 0.5,
        }}
      >
        <InputBase
          fullWidth
          inputRef={inputRef}
          maxRows={8}
          multiline
          onChange={(event) => onContentChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('messaging.message_placeholder')}
          sx={{ px: 0.5 }}
          value={content}
        />
      </MessageInputShell>

      <SmallLoadingButton
        color="brandV2Yellow"
        disabled={!canSend}
        loading={isSending}
        onClick={onSend}
        startIcon={<SendIcon sx={{ fontSize: 16 }} />}
        sx={{ height: `${MESSAGE_CONTROL_HEIGHT_IN_PX}px` }}
      >
        {t('actions.send')}
      </SmallLoadingButton>

      <Box sx={{ gridColumn: hasLeadingControl ? 2 : 1, minWidth: 0 }}>
        <MessageLengthHint content={content} />
      </Box>
    </Box>
  );
}
